import React from 'react';
import { ArrowLeft, HelpCircle, Globe, Database, FileText, RefreshCw, Download, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function HelpPage() {
  const navigate = useNavigate();

  // Composant Badge interne pour la démo de la légende
  const DemoBadge = ({ type, icon: Icon, text }) => {
    const colors = {
      live: "bg-blue-100 text-blue-800 border-blue-200",
      local: "bg-emerald-100 text-emerald-800 border-emerald-200",
      custom: "bg-amber-100 text-amber-800 border-amber-200"
    };
    return (
      <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${colors[type]} flex items-center gap-2 w-fit`}>
        <Icon className="w-3 h-3" /> {text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 font-sans text-slate-900">
      <div className="max-w-4xl mx-auto">

        <button
          onClick={() => navigate(-1)}
          className="mb-8 text-slate-500 hover:text-blue-600 flex items-center gap-2 transition-colors font-medium bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm w-fit"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-slate-200">

          <div className="flex items-center gap-4 mb-10 pb-8 border-b border-slate-100">
            <div className="bg-indigo-50 p-3 rounded-xl">
              <HelpCircle className="w-8 h-8 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Centre d'Aide</h1>
              <p className="text-slate-500 mt-1">Guide d'utilisation de Réglo-veille</p>
            </div>
          </div>

          <div className="space-y-12">

            {/* SECTION 1 : LÉGENDE DES SOURCES */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                1. Comprendre les sources de données
              </h2>
              <p className="text-slate-600 mb-6">
                Pour garantir performance et fiabilité, notre application combine plusieurs sources.
                Voici comment les identifier dans votre flux :
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
                  <div className="mb-3"><DemoBadge type="live" icon={Globe} text="Légifrance Live" /></div>
                  <h3 className="font-semibold text-sm mb-1">Source Officielle mise à jour</h3>
                  <p className="text-xs text-slate-500">Provient directement des publications de Légifrance en temps réel. Donnée la plus fraîche possible !</p>
                </div>

                <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
                  <div className="mb-3"><DemoBadge type="local" icon={Database} text="Base Locale" /></div>
                  <h3 className="font-semibold text-sm mb-1">Donnée Optimisée</h3>
                  <p className="text-xs text-slate-500">Texte déjà synchronisé et stocké sur nos serveurs sécurisés. Affichage instantané !</p>
                </div>

                <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
                  <div className="mb-3"><DemoBadge type="custom" icon={FileText} text="Règle Interne" /></div>
                  <h3 className="font-semibold text-sm mb-1">Votre Personnalisation Entreprise</h3>
                  <p className="text-xs text-slate-500">Règlementation spécifique que vous avez ajoutée manuellement, pour votre propre organisation.</p>
                </div>
              </div>
            </section>

            {/* SECTION 2 : SYNCHRONISATION */}
            <section className="border-t border-slate-100 pt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-blue-600" /> La Synchronisation
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Lorsque vous cliquez sur le bouton <strong>Sync Légifrance</strong>, l'application interroge simultanément :
              </p>
              <ul className="list-disc pl-5 mt-3 space-y-2 text-slate-600">
                <li>Les serveurs officiels du gouvernement (DILA).</li>
                <li>Votre base de données privée.</li>
              </ul>
              <div className="mt-4 p-3 bg-blue-50 text-blue-800 text-sm rounded-lg border border-blue-100">
                ℹ️ <strong>Conseil :</strong> Si un texte semble manquant, vérifiez vos thèmes sélectionnés en haut du tableau de bord et relancez une synchronisation.
              </div>
            </section>

            {/* SECTION 3 : FONCTIONNALITÉS */}
            <section className="border-t border-slate-100 pt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Fonctionnalités Clés</h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="bg-gray-100 p-2 h-fit rounded-lg"><Download className="w-5 h-5 text-gray-600" /></div>
                  <div>
                    <h3 className="font-semibold">Export PDF</h3>
                    <p className="text-sm text-slate-500">Générez un rapport formel contenant tous les textes affichés à l'écran pour vos réunions CSE ou RH.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-gray-100 p-2 h-fit rounded-lg"><MessageSquare className="w-5 h-5 text-gray-600" /></div>
                  <div>
                    <h3 className="font-semibold">Assistant IA</h3>
                    <p className="text-sm text-slate-500">Posez des questions en langage naturel (ex: "Quel est le délai de préavis ?"). L'IA analyse les textes affichés pour vous répondre.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* SECTION 4 : SUPPORT */}
            <section className="border-t border-slate-100 pt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Besoin d'aide supplémentaire ?</h2>
              <p className="text-slate-600 mb-4">
                Notre équipe support est disponible pour les abonnés Pro et Enterprise.
              </p>
              <a href="mailto:support@quest-ia.fr" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-slate-900 hover:bg-slate-800">
                Contacter le support
              </a>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}